<template>
  <div :class="device.isMobile ? 'py-2' : 'px-4 pb-2 pt-4'">
    <!-- Loading state -->
    <empty-state v-if="isLoading" :title="$t('loading')" :loading="true" />

    <!-- Empty state: No restaurants selected -->
    <empty-state
      v-else-if="!selectedRestaurants.length"
      icon="mdi-store"
      :title="$t('no_restaurants_selected')"
      :subtitle="$t('select_restaurants_to_view_report')"
    />

    <!-- Empty state: No data -->
    <empty-state
      v-else-if="!meals.length"
      icon="mdi-food-off"
      :title="$t('no_data')"
      :subtitle="$t('no_meals_for_selected_period')"
    />

    <!-- Desktop View -->
    <v-data-table
      v-else-if="!device.isMobile"
      :headers="headers"
      :items="customSearch(meals, headers, search)"
      class="ordera-box-shadow striped-table"
      hide-default-footer
      disable-pagination
      mobile-breakpoint="0"
    >
      <template v-slot:item.name="{ item }">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <span v-bind="attrs" v-on="on">
              {{ toLatin(item.name) }}
            </span>
          </template>
          <span>{{ item.id }}</span>
        </v-tooltip>
      </template>
      <template #body.append>
        <tr class="font-weight-bold totals-row">
          <td colspan="2" v-if="!device.isMobile"></td>

          <td>{{ $t("total") }}</td>
          <td v-for="shift in shiftsWithData" :key="shift" class="text-center">
            {{ formatShiftQuantity(totals[getShiftKey(shift)]) }}
          </td>
          <td class="text-center">{{ totals.total }}</td>
        </tr>
      </template>
      <!-- Dynamic shift templates -->
      <template
        v-for="shift in shiftsWithData"
        v-slot:[`item.${getShiftKey(shift)}`]="{ item }"
      >
        <span>
          <span v-if="item[getShiftKey(shift)].customerQuantity > 0">
            {{ item[getShiftKey(shift)].customerQuantity }}
          </span>
          <span v-if="item[getShiftKey(shift)].restaurantQuantity > 0">
            <span v-if="item[getShiftKey(shift)].customerQuantity > 0"> + </span>
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <span v-bind="attrs" v-on="on"
                  >{{ item[getShiftKey(shift)].restaurantQuantity }}R</span
                >
              </template>
              <span>{{ $t("ordered_by_restaurant") }}</span>
            </v-tooltip>
          </span>
          <span v-if="item[getShiftKey(shift)].totalQuantity === 0">0</span>
        </span>
      </template>
    </v-data-table>

    <!-- Mobile View -->
    <mobile-data-cards
      v-else
      :headers="mobileHeaders"
      :items="customSearch(meals, headers, search)"
      item-key="id"
      :fields-per-row="null"
      :bold-fields="['name']"
      :show-row-number="false"
      class="px-3"
    >
      <!-- Dynamic shift templates for mobile -->
      <template
        v-for="shift in shiftsWithData"
        v-slot:[`item.${getShiftKey(shift)}`]="{ item }"
      >
        <span>
          <span v-if="item[getShiftKey(shift)].customerQuantity > 0">
            {{ item[getShiftKey(shift)].customerQuantity }}
          </span>
          <span v-if="item[getShiftKey(shift)].restaurantQuantity > 0">
            <span v-if="item[getShiftKey(shift)].customerQuantity > 0"> + </span>
            {{ item[getShiftKey(shift)].restaurantQuantity }}R
          </span>
          <span v-if="item[getShiftKey(shift)].totalQuantity === 0">0</span>
        </span>
      </template>
    </mobile-data-cards>
  </div>
</template>

<script>
import reportService from "@/services/report";
import hasPrinting from "@/mixins/hasPrinting";
import hasReportPrinting from "@/mixins/report/hasReportPrinting";
import EmptyState from "@/components/global/EmptyState.vue";
import MobileDataCards from "@/components/global/MobileDataCards.vue";
import hasExcel from "@/mixins/hasExcel";
import hasSnackbar from "@/mixins/hasSnackbar";
import hasMobileHeaders from "@/mixins/report/hasMobileHeaders";
import { SHIFTS, getShiftKey } from "@/constants/shifts";

export default {
  name: "CompanyMultiRestaurantTabRestaurantMeals",
  mixins: [hasPrinting, hasReportPrinting, hasExcel, hasSnackbar, hasMobileHeaders],
  components: {
    EmptyState,
    MobileDataCards,
  },
  data() {
    return {
      packages: null,
      isLoading: false,
    };
  },
  mounted() {
    this.fetchData();
  },
  methods: {
    getShiftKey,
    createEmptyShift() {
      return { customerQuantity: 0, restaurantQuantity: 0, totalQuantity: 0 };
    },
    formatShiftQuantity(shiftData) {
      if (!shiftData || shiftData.totalQuantity === 0) return "0";
      const { customerQuantity = 0, restaurantQuantity = 0 } = shiftData;
      const parts = [];
      if (customerQuantity > 0) parts.push(customerQuantity);
      if (restaurantQuantity > 0) parts.push(`${restaurantQuantity}R`);
      return parts.join(" + ") || "0";
    },
    getFormattedItems() {
      return this.meals.map((item, index) => ({
        num: index + 1,
        name: item.name,
        ...this.shiftsWithData.reduce((acc, shift) => {
          acc[this.getShiftKey(shift)] = this.formatShiftQuantity(
            item[this.getShiftKey(shift)]
          );
          return acc;
        }, {}),
        total: item.total,
      }));
    },
    async fetchData() {
      if (!this.companyId || this.selectedRestaurants.length === 0) {
        this.packages = [];
        return;
      }

      this.isLoading = true;
      try {
        const params = {
          date: this.dates.from,
          date2: this.dates.to,
          company: this.companyId,
        };

        // Fetch data for each selected restaurant
        const promises = this.selectedRestaurants.map((restaurantId) =>
          reportService.getPackagesForMoreRestaurant(restaurantId, params)
        );

        const responses = await Promise.all(promises);

        // Combine all responses
        this.packages = responses
          .map((response, index) => {
            if (Array.isArray(response) && response.length > 0) {
              return {
                ...response[0],
                id: this.selectedRestaurants[index],
              };
            }
            return null;
          })
          .filter(Boolean);
      } catch (error) {
        console.error("Error fetching meals:", error);
        this.showErrorSnackbar(error, this.$t("error_loading_report"));
      } finally {
        this.isLoading = false;
      }
    },
  },
  computed: {
    companyId() {
      return this.$store.state.company?.id;
    },
    restaurants() {
      return this.$store.state.company?.restaurants || [];
    },
    selectedRestaurants() {
      return this.$store.state.report.selectedRestaurants || [];
    },
    dates() {
      const { from, to } = this.$store.state.report.filters.dates;
      return { from, to };
    },
    selectedShifts() {
      return this.$store.state.report.filters.selectedShifts;
    },
    search() {
      return this.$store.state.report.filters.search;
    },
    meals() {
      if (!this.packages || !Array.isArray(this.packages) || this.packages.length === 0) {
        return [];
      }

      // Aggregate meals from all restaurants
      const mealMap = new Map();

      this.packages.forEach((restaurantData) => {
        if (!restaurantData.meals) return;

        restaurantData.meals.forEach((meal) => {
          const key = meal.id || `name_${meal.name}`;

          if (!mealMap.has(key)) {
            const shifts = SHIFTS.reduce(
              (acc, s) => ({ ...acc, [s.key]: this.createEmptyShift() }),
              {}
            );
            mealMap.set(key, {
              id: meal.id,
              name: this.toLatin(meal.name),
              type: meal.type || null,
              num: mealMap.size + 1,
              ...shifts,
              total: 0,
            });
          }

          const mealData = mealMap.get(key);

          // Aggregate each shift
          SHIFTS.forEach((shift) => {
            const shiftKey = shift.key;
            mealData[shiftKey].customerQuantity += meal[shiftKey]?.customerQuantity || 0;
            mealData[shiftKey].restaurantQuantity +=
              meal[shiftKey]?.restaurantQuantity || 0;
            mealData[shiftKey].totalQuantity += meal[shiftKey]?.totalQuantity || 0;
          });

          mealData.total += meal.total || 0;
        });
      });

      return Array.from(mealMap.values());
    },
    totals() {
      const totals = SHIFTS.reduce(
        (acc, s) => ({ ...acc, [s.key]: this.createEmptyShift() }),
        { total: 0 }
      );

      this.meals.forEach((meal) => {
        SHIFTS.forEach((shift) => {
          const shiftKey = shift.key;
          totals[shiftKey].customerQuantity += meal[shiftKey]?.customerQuantity || 0;
          totals[shiftKey].restaurantQuantity += meal[shiftKey]?.restaurantQuantity || 0;
          totals[shiftKey].totalQuantity += meal[shiftKey]?.totalQuantity || 0;
        });
        totals.total += meal.total || 0;
      });

      return totals;
    },
    shiftsWithData() {
      // selectedShifts is an array from store [1, 2, 3] or empty []
      if (Array.isArray(this.selectedShifts) && this.selectedShifts.length > 0) {
        return this.selectedShifts.sort((a, b) => a - b);
      }
      // If no shifts selected, show all
      return [1, 2, 3, 4];
    },
    headers() {
      const baseHeaders = [
        { text: this.$t("r_b"), value: "num", sortable: false },
        { text: this.$t("meal"), value: "name" },
        { text: this.$t("type"), value: "type" },
      ];

      const shiftHeaders = SHIFTS.filter((shift) =>
        this.shiftsWithData.includes(shift.id)
      ).map((shift) => ({
        text: this.$t(shift.label),
        value: shift.key,
        align: "center",
      }));

      const totalHeader = [{ text: this.$t("total"), value: "total", align: "center" }];

      return [...baseHeaders, ...shiftHeaders, ...totalHeader];
    },
    mobileHeaders() {
      const baseHeaders = [{ text: this.$t("meal"), value: "name", cols: 12 }];

      const shiftHeaders = SHIFTS.filter((shift) =>
        this.shiftsWithData.includes(shift.id)
      ).map((shift) => ({
        text: this.$t(shift.label),
        value: shift.key,
        align: "center",
        cols: 3,
      }));

      const totalHeader = [
        { text: this.$t("total"), value: "total", align: "center", cols: 3 },
      ];

      return [...baseHeaders, ...shiftHeaders, ...totalHeader];
    },
  },
  watch: {
    dates: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
    selectedRestaurants: {
      handler() {
        this.fetchData();
      },
      deep: true,
    },
    isExcelDownloading() {
      if (!this.isExcelDownloading) return;
      this.downloadExcel({
        fileName: [this.$t("report"), this.$t("meals")].join(" ") + ".xlsx",
        sheets: [
          {
            name: this.$t("meals"),
            headers: this.headers,
            items: this.getFormattedItems(),
            title: [this.$t("report"), this.$t("meals")].join(" "),
            subtitle: this.subtitle,
          },
        ],
      });
    },
    isPrinting(isPrinting) {
      if (!isPrinting) return;
      const restaurantNames = this.selectedRestaurants
        .map((restaurantId) => {
          const restaurant = this.restaurants.find((r) => r.id === restaurantId);
          return restaurant ? this.toLatin(restaurant.name) : "";
        })
        .filter(Boolean)
        .join(", ");

      this.printDocument({
        tables: [
          { headers: this.headers, items: this.getFormattedItems(), showCaption: true },
        ],
        title: [this.$t("report"), this.$t("meals")].join(": "),
        subtitle: this.subtitle,
        afterTableHtml: `<div style="margin-top: 20px; padding-top: 10px; border-top: 1px solid #ddd;">
          <strong>${this.$t("restaurants")}:</strong> ${restaurantNames}
        </div>`,
        hideDatetime: true,
      });
    },
  },
};
</script>
